import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, Users, FileText, FileSignature, MessageSquare,
  Edit3, Save, Loader2, Check, X, Image, Bold, Italic, List,
  Link as LinkIcon, Heading1, Heading2, Eye, Trash2, Settings, CreditCard, Euro,
  Plus, TrendingDown, ShoppingCart, Calculator, PieChart, Send, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get auth token helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('tannou_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Accounting categories and pages
const accountingPages = [
  { id: 'home', label: 'Accueil' },
  { id: 'formation', label: 'Formation' },
  { id: 'pme', label: 'PME' },
  { id: 'freelance', label: 'Freelance' },
  { id: 'employes', label: 'Employés' },
  { id: 'siteweb', label: 'Site Web' },
  { id: 'general', label: 'Général' },
];

const accountingCategories = ['Hébergement', 'Marketing', 'Développement', 'Design', 'Contenu', 'Outils', 'Formation', 'Autre'];

export const Admin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Permet a un lien externe (ex: le menu "Comptabilité" du header) d'arriver
  // directement sur un onglet precis via /admin?tab=accounting.
  const initialTab = new URLSearchParams(window.location.search).get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [stats, setStats] = useState({ clients: 0, quotes: 0, contracts: 0, testimonials: 0 });
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteEditorOpen, setQuoteEditorOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [accountingSummary, setAccountingSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: '', percent: 50, note: '' });
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [accountingDialogOpen, setAccountingDialogOpen] = useState(false);
  const [accountingFilter, setAccountingFilter] = useState('all');
  const [accountingForm, setAccountingForm] = useState({
    page: 'general',
    type: 'expense',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Redirige vers /login si le token est absent ou invalide. Auparavant
  // cette page ne verifiait rien cote client : elle "marchait" seulement
  // parce que le serveur n'exigeait aucune authentification. Maintenant que
  // le serveur exige un vrai jeton admin, un visiteur sans jeton doit etre
  // renvoye se connecter plutot que de voir une page vide/en erreur.
  const requireAuth = async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      navigate('/login');
      return false;
    }
    try {
      const res = await axios.post(`${API}/auth/verify`, {}, { headers });
      if (!res.data.valid) {
        navigate('/login');
        return false;
      }
      return true;
    } catch (e) {
      navigate('/login');
      return false;
    }
  };

  const fetchData = async () => {
    const authed = await requireAuth();
    if (!authed) return;

    try {
      const headers = getAuthHeaders();
      const [statsRes, clientsRes, quotesRes, contractsRes, testimonialsRes, accountingRes, summaryRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/quotes`, { headers }),
        axios.get(`${API}/contracts`, { headers }),
        axios.get(`${API}/testimonials?approved_only=false`, { headers }),
        axios.get(`${API}/accounting`, { headers }),
        axios.get(`${API}/accounting/summary`, { headers }),
        axios.get(`${API}/payments/transactions`, { headers })
      ]);

      setStats(statsRes.data);
      setClients(clientsRes.data);
      setQuotes(quotesRes.data);
      setContracts(contractsRes.data);
      setTestimonials(testimonialsRes.data);
      setAccountingEntries(accountingRes.data);
      setAccountingSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  // ── Devis (2026-09-10) : editeur pour completer un devis simule cote client
  // avant envoi - typiquement ajouter la ligne de maintenance/suivi, qui
  // n'apparait jamais dans le simulateur public. ──────────────────────────────
  const openQuoteEditor = (quote) => {
    setEditingQuote(quote);
    setEditItems(quote.items.map((i) => ({ ...i })));
    setEditNotes(quote.notes || '');
    setQuoteEditorOpen(true);
  };

  const updateEditItem = (index, field, value) => {
    setEditItems((items) => items.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const removeEditItem = (index) => {
    setEditItems((items) => items.filter((_, i) => i !== index));
  };

  const addEditItem = (description = '', unit_price = 0) => {
    setEditItems((items) => [...items, { description, quantity: 1, unit_price }]);
  };

  const editTotal = editItems.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  const handleSaveQuote = async () => {
    if (!editingQuote) return;
    setSavingQuote(true);
    try {
      const payload = {
        client_id: editingQuote.client_id,
        items: editItems.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 1,
          unit_price: Number(i.unit_price) || 0,
        })),
        notes: editNotes,
      };
      await axios.put(`${API}/quotes/${editingQuote.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Devis mis a jour');
      fetchData();
      setQuoteEditorOpen(false);
    } catch (error) {
      toast.error(t('error'));
    }
    setSavingQuote(false);
  };

  const handleMarkQuoteSent = async () => {
    if (!editingQuote) return;
    setSavingQuote(true);
    try {
      await handleSaveQuote();
      await axios.put(`${API}/quotes/${editingQuote.id}/status`, { status: 'sent' }, { headers: getAuthHeaders() });
      toast.success('Devis marque comme envoye au client');
      fetchData();
      setQuoteEditorOpen(false);
    } catch (error) {
      toast.error(t('error'));
    }
    setSavingQuote(false);
  };

  const copyQuoteLink = (quoteId) => {
    const url = `${window.location.origin}/quote/${quoteId}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copie');
  };

  const clientForQuote = (quote) => clients.find((c) => c.id === quote.client_id);

  // ── Codes promo ─────────────────────────────────────────────────────────────
  const loadPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/promo-codes`, { headers: getAuthHeaders() });
      setPromoCodes(res.data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { if (activeTab === 'promo') loadPromoCodes(); }, [activeTab]);

  const createPromoCode = async () => {
    if (!newPromo.code.trim()) { toast.error('Code obligatoire'); return; }
    try {
      await axios.post(`${API}/promo-codes`, { code: newPromo.code, percent: Number(newPromo.percent) || 50, note: newPromo.note, applies_to: 'balance' }, { headers: getAuthHeaders() });
      setNewPromo({ code: '', percent: 50, note: '' });
      toast.success('Code cree');
      loadPromoCodes();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? 'Ce code existe deja' : 'Echec');
    }
  };

  const togglePromoActive = async (pc) => {
    try {
      await axios.put(`${API}/promo-codes/${pc.id}`, { ...pc, active: !pc.active }, { headers: getAuthHeaders() });
      loadPromoCodes();
    } catch { toast.error('Echec'); }
  };

  // ── Reglages du site (rubriques visibles dans le menu) ──────────────────────
  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSiteSettings(res.data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { if (activeTab === 'settings' && !siteSettings) loadSettings(); }, [activeTab]);

  const toggleSettingField = (key) => setSiteSettings((s) => ({ ...s, [key]: !s[key] }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, siteSettings, { headers: getAuthHeaders() });
      toast.success('Réglages enregistrés');
    } catch { toast.error('Échec de l’enregistrement'); }
    setSavingSettings(false);
  };

  const handleApproveTestimonial = async (id) => {
    try {
      await axios.put(`${API}/testimonials/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Témoignage approuvé');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: getAuthHeaders() });
      toast.success('Témoignage supprimé');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Accounting functions
  const handleAddAccountingEntry = async (e) => {
    e.preventDefault();
    if (!accountingForm.description || !accountingForm.amount) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }
    try {
      await axios.post(`${API}/accounting`, {
        ...accountingForm,
        amount: parseFloat(accountingForm.amount)
      }, { headers: getAuthHeaders() });
      toast.success('Entrée ajoutée');
      setAccountingDialogOpen(false);
      setAccountingForm({
        page: 'general',
        type: 'expense',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        category: ''
      });
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteAccountingEntry = async (id) => {
    try {
      await axios.delete(`${API}/accounting/${id}`, { headers: getAuthHeaders() });
      toast.success('Entrée supprimée');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const filteredAccountingEntries = accountingFilter === 'all' 
    ? accountingEntries 
    : accountingEntries.filter(e => e.page === accountingFilter);

  const totalExpenses = filteredAccountingEntries
    .filter(e => e.type === 'expense')
    .reduce((sum, e) => sum + e.amount, 0);

  const totalPurchases = filteredAccountingEntries
    .filter(e => e.type === 'purchase')
    .reduce((sum, e) => sum + e.amount, 0);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen pt-20 bg-slate-100" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-white border-r hidden lg:block">
          <div className="p-6">
            <h2 className="font-heading font-bold text-xl text-slate-900">Admin AGENTORY</h2>
          </div>
          <nav className="px-4 space-y-1">
            {[
              { id: 'dashboard', icon: LayoutDashboard, label: t('dashboard') },
              { id: 'clients', icon: Users, label: t('clients') },
              { id: 'quotes', icon: FileText, label: t('quotes') },
              { id: 'contracts', icon: FileSignature, label: t('contracts') },
              { id: 'payments', icon: CreditCard, label: 'Paiements' },
              { id: 'accounting', icon: Euro, label: 'Comptabilité' },
              { id: 'testimonials', icon: MessageSquare, label: t('testimonials') },
              { id: 'content', icon: Edit3, label: t('content') },
              { id: 'promo', icon: CreditCard, label: 'Codes promo' },
              { id: 'settings', icon: Settings, label: 'Réglages du site' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === item.id
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
            <a
              href="/cahier-des-charges"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="admin-nav-brief"
            >
              <FileSignature className="w-5 h-5" />
              Cahier des charges
            </a>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Mobile Tabs */}
          <div className="lg:hidden mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="dashboard">Accueil</TabsTrigger>
                <TabsTrigger value="clients">Clients</TabsTrigger>
                <TabsTrigger value="content">Contenu</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Dashboard */}
              {activeTab === 'dashboard' && (
                <div className="space-y-6" data-testid="admin-dashboard">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">{t('dashboard')}</h1>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={Users} label={t('clients')} value={stats.clients} color="bg-blue-500" />
                    <StatCard icon={FileText} label={t('quotes')} value={stats.quotes} color="bg-emerald-500" />
                    <StatCard icon={FileSignature} label={t('contracts')} value={stats.contracts} color="bg-purple-500" />
                    <StatCard icon={MessageSquare} label={t('testimonials')} value={stats.testimonials} color="bg-amber-500" />
                  </div>
                </div>
              )}

              {/* Clients */}
              {activeTab === 'clients' && (
                <div className="space-y-6" data-testid="admin-clients">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">{t('clients')}</h1>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nom</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Secteur</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clients.map((client) => (
                            <TableRow key={client.id}>
                              <TableCell className="font-medium">{client.name}</TableCell>
                              <TableCell>{client.email}</TableCell>
                              <TableCell className="capitalize">{client.branch}</TableCell>
                              <TableCell>
                                <Badge variant={client.status === 'contracted' ? 'default' : 'secondary'}>
                                  {client.status}
                                </Badge>
                              </TableCell>
                              <TableCell>{new Date(client.created_at).toLocaleDateString('fr-FR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Quotes */}
              {activeTab === 'quotes' && (
                <div className="space-y-6" data-testid="admin-quotes">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">{t('quotes')}</h1>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Client</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {quotes.map((quote) => {
                            const client = clientForQuote(quote);
                            return (
                            <TableRow key={quote.id}>
                              <TableCell>
                                {client ? (
                                  <div>
                                    <div className="font-medium">{client.name}</div>
                                    <div className="text-xs text-slate-500">{client.email}</div>
                                  </div>
                                ) : (
                                  <span className="font-mono text-sm text-slate-400">{quote.id.substring(0, 8)}</span>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{quote.total?.toFixed(2)} €</TableCell>
                              <TableCell>
                                <Badge variant={quote.status === 'accepted' ? 'default' : quote.status === 'sent' ? 'outline' : 'secondary'}>
                                  {quote.status === 'draft' ? 'Brouillon' : quote.status === 'sent' ? 'Envoyé' : quote.status === 'accepted' ? 'Accepté' : quote.status}
                                </Badge>
                              </TableCell>
                              <TableCell>{new Date(quote.created_at).toLocaleDateString('fr-FR')}</TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => openQuoteEditor(quote)} data-testid={`quote-edit-${quote.id.substring(0, 8)}`}>
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Editeur de devis : sert surtout a completer un devis
                     simule cote client (voir /devis-simulateur) avec les
                     frais de suivi/maintenance avant envoi definitif. */}
                  <Dialog open={quoteEditorOpen} onOpenChange={setQuoteEditorOpen}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Devis {editingQuote && clientForQuote(editingQuote)?.name ? `- ${clientForQuote(editingQuote).name}` : ''}</DialogTitle>
                        <DialogDescription>
                          {editingQuote && clientForQuote(editingQuote) ? (
                            <>
                              {clientForQuote(editingQuote).email} · {clientForQuote(editingQuote).phone}
                              <br />
                              <span className="italic">{clientForQuote(editingQuote).needs}</span>
                            </>
                          ) : 'Ajustez les lignes puis enregistrez ou envoyez au client.'}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-3">
                        {editItems.map((item, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={item.description}
                              onChange={(e) => updateEditItem(index, 'description', e.target.value)}
                              className="flex-1"
                              placeholder="Description"
                            />
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateEditItem(index, 'quantity', e.target.value)}
                              className="w-16"
                              min="1"
                            />
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateEditItem(index, 'unit_price', e.target.value)}
                              className="w-24"
                              step="0.01"
                            />
                            <span className="text-sm text-slate-400">€</span>
                            <Button variant="ghost" size="sm" onClick={() => removeEditItem(index)} data-testid={`quote-item-remove-${index}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => addEditItem()} className="gap-1">
                            <Plus className="w-4 h-4" /> Ligne
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addEditItem('Maintenance mensuelle', 49)} className="gap-1">
                            <Plus className="w-4 h-4" /> Maintenance
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Notes internes</Label>
                        <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-heading font-bold text-xl">Total : {editTotal.toFixed(2)} €</span>
                        {editingQuote && (
                          <Button variant="ghost" size="sm" onClick={() => copyQuoteLink(editingQuote.id)} className="gap-1 text-slate-500">
                            <Copy className="w-4 h-4" /> Copier le lien client
                          </Button>
                        )}
                      </div>

                      <DialogFooter>
                        <Button variant="outline" onClick={handleSaveQuote} disabled={savingQuote} className="gap-2">
                          {savingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Enregistrer
                        </Button>
                        <Button onClick={handleMarkQuoteSent} disabled={savingQuote} className="bg-primary hover:bg-primary-hover text-white gap-2">
                          {savingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Enregistrer et marquer envoyé
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {/* Contracts */}
              {activeTab === 'contracts' && (
                <div className="space-y-6" data-testid="admin-contracts">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">{t('contracts')}</h1>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Signé le</TableHead>
                            <TableHead>Créé le</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contracts.map((contract) => (
                            <TableRow key={contract.id}>
                              <TableCell className="font-mono text-sm">{contract.id.substring(0, 8)}</TableCell>
                              <TableCell>
                                <Badge variant={contract.status === 'signed' ? 'default' : 'secondary'}>
                                  {contract.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString('fr-FR') : '-'}
                              </TableCell>
                              <TableCell>{new Date(contract.created_at).toLocaleDateString('fr-FR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Payments */}
              {activeTab === 'payments' && (
                <div className="space-y-6" data-testid="admin-payments">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">Paiements</h1>
                  
                  {/* Payment Stats */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                            <Euro className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Total reçu</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0).toFixed(2)} €
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                            <CreditCard className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">En attente</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {transactions.filter(t => t.payment_status === 'pending').length}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                            <Check className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Transactions réussies</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {transactions.filter(t => t.payment_status === 'paid').length}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Transactions Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Historique des transactions</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Package</TableHead>
                            <TableHead>Montant</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Session ID</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                Aucune transaction
                              </TableCell>
                            </TableRow>
                          ) : (
                            transactions.map((transaction) => (
                              <TableRow key={transaction.id}>
                                <TableCell>{new Date(transaction.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                <TableCell className="font-medium">{transaction.package_name}</TableCell>
                                <TableCell>{transaction.amount.toFixed(2)} €</TableCell>
                                <TableCell>
                                  <Badge variant={
                                    transaction.payment_status === 'paid' ? 'default' : 
                                    transaction.payment_status === 'pending' ? 'secondary' : 'destructive'
                                  }>
                                    {transaction.payment_status === 'paid' ? 'Payé' : 
                                     transaction.payment_status === 'pending' ? 'En attente' : 'Échoué'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-mono text-xs">{transaction.session_id?.substring(0, 20)}...</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Accounting */}
              {activeTab === 'accounting' && (
                <div className="space-y-6" data-testid="admin-accounting">
                  <div className="flex items-center justify-between">
                    <h1 className="font-heading font-bold text-2xl text-slate-900">Comptabilité</h1>
                    <Dialog open={accountingDialogOpen} onOpenChange={setAccountingDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-primary hover:bg-primary-hover text-white rounded-full gap-2">
                          <Plus className="w-5 h-5" />
                          Nouvelle entrée
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Ajouter une entrée</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddAccountingEntry} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Page</Label>
                              <Select
                                value={accountingForm.page}
                                onValueChange={(value) => setAccountingForm(prev => ({ ...prev, page: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {accountingPages.map((page) => (
                                    <SelectItem key={page.id} value={page.id}>{page.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Type</Label>
                              <Select
                                value={accountingForm.type}
                                onValueChange={(value) => setAccountingForm(prev => ({ ...prev, type: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="expense">Dépense</SelectItem>
                                  <SelectItem value="purchase">Achat</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Description *</Label>
                            <Input
                              value={accountingForm.description}
                              onChange={(e) => setAccountingForm(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Description de l'entrée"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Montant (€) *</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={accountingForm.amount}
                                onChange={(e) => setAccountingForm(prev => ({ ...prev, amount: e.target.value }))}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Date</Label>
                              <Input
                                type="date"
                                value={accountingForm.date}
                                onChange={(e) => setAccountingForm(prev => ({ ...prev, date: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Catégorie</Label>
                            <Select
                              value={accountingForm.category}
                              onValueChange={(value) => setAccountingForm(prev => ({ ...prev, category: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner..." />
                              </SelectTrigger>
                              <SelectContent>
                                {accountingCategories.map((cat) => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">
                            Ajouter
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                            <TrendingDown className="w-6 h-6 text-red-500" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Dépenses</p>
                            <p className="text-2xl font-bold text-slate-900">{totalExpenses.toFixed(2)} €</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <ShoppingCart className="w-6 h-6 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Achats</p>
                            <p className="text-2xl font-bold text-slate-900">{totalPurchases.toFixed(2)} €</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                            <Calculator className="w-6 h-6 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Total</p>
                            <p className="text-2xl font-bold text-slate-900">{(totalExpenses + totalPurchases).toFixed(2)} €</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Filter */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant={accountingFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAccountingFilter('all')}
                    >
                      Tout
                    </Button>
                    {accountingPages.map((page) => (
                      <Button
                        key={page.id}
                        variant={accountingFilter === page.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAccountingFilter(page.id)}
                      >
                        {page.label}
                      </Button>
                    ))}
                  </div>

                  {/* Summary by Page */}
                  {Object.keys(accountingSummary).length > 0 && accountingFilter === 'all' && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <PieChart className="w-5 h-5" />
                          Résumé par page
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {Object.entries(accountingSummary).map(([page, data]) => (
                            <div key={page} className="p-4 bg-slate-50 rounded-lg">
                              <h4 className="font-medium text-slate-900 capitalize mb-2">{page}</h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Dépenses</span>
                                  <span className="text-red-600">{data.expenses?.toFixed(2) || '0.00'} €</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Achats</span>
                                  <span className="text-blue-600">{data.purchases?.toFixed(2) || '0.00'} €</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Entries Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Entrées</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Page</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Catégorie</TableHead>
                            <TableHead className="text-right">Montant</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAccountingEntries.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                Aucune entrée
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAccountingEntries.map((entry) => (
                              <TableRow key={entry.id}>
                                <TableCell>{new Date(entry.date).toLocaleDateString('fr-FR')}</TableCell>
                                <TableCell className="capitalize">{entry.page}</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    entry.type === 'expense' 
                                      ? 'bg-red-100 text-red-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {entry.type === 'expense' ? 'Dépense' : 'Achat'}
                                  </span>
                                </TableCell>
                                <TableCell>{entry.description}</TableCell>
                                <TableCell>{entry.category || '-'}</TableCell>
                                <TableCell className="text-right font-medium">{entry.amount.toFixed(2)} €</TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteAccountingEntry(entry.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Testimonials */}
              {activeTab === 'testimonials' && (
                <div className="space-y-6" data-testid="admin-testimonials">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">{t('testimonials')}</h1>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Auteur</TableHead>
                            <TableHead>Contenu</TableHead>
                            <TableHead>Note</TableHead>
                            <TableHead>Approuvé</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {testimonials.map((testimonial) => (
                            <TableRow key={testimonial.id}>
                              <TableCell className="font-medium">{testimonial.author_name}</TableCell>
                              <TableCell className="max-w-xs truncate">{testimonial.content}</TableCell>
                              <TableCell>{testimonial.rating}/5</TableCell>
                              <TableCell>
                                {testimonial.approved ? (
                                  <Check className="w-5 h-5 text-green-500" />
                                ) : (
                                  <X className="w-5 h-5 text-slate-300" />
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  {!testimonial.approved && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleApproveTestimonial(testimonial.id)}
                                      data-testid={`approve-testimonial-${testimonial.id}`}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-500"
                                    onClick={() => handleDeleteTestimonial(testimonial.id)}
                                    data-testid={`delete-testimonial-${testimonial.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Content Editor (WYSIWYG) */}
              {activeTab === 'content' && (
                <WYSIWYGEditor />
              )}

              {/* Codes promo */}
              {activeTab === 'promo' && (
                <div className="space-y-6" data-testid="admin-promo">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">Codes promo</h1>
                  <p className="text-sm text-slate-500 max-w-2xl">
                    La reduction s'applique sur le solde (hors forfait cadrage). Tu l'actives sur le devis
                    dans le cahier des charges, apres avoir verifie le justificatif du client.
                  </p>

                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Reduction</TableHead>
                            <TableHead>Utilisations</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {promoCodes.map((pc) => (
                            <TableRow key={pc.id}>
                              <TableCell className="font-mono font-semibold">{pc.code}</TableCell>
                              <TableCell>-{pc.percent}% sur le solde</TableCell>
                              <TableCell>{pc.uses_count || 0}</TableCell>
                              <TableCell>
                                <Badge variant={pc.active ? 'default' : 'secondary'}>{pc.active ? 'Actif' : 'Desactive'}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => togglePromoActive(pc)}>
                                  {pc.active ? 'Desactiver' : 'Reactiver'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {promoCodes.find((p) => p.code === 'KAMAGUY')?.note && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <strong>KAMAGUY - rappel justificatif :</strong> {promoCodes.find((p) => p.code === 'KAMAGUY').note}
                    </div>
                  )}

                  <Card>
                    <CardHeader><CardTitle className="text-base">Nouveau code</CardTitle></CardHeader>
                    <CardContent className="grid sm:grid-cols-3 gap-3 items-end">
                      <div className="space-y-2">
                        <Label>Code</Label>
                        <Input value={newPromo.code} onChange={(e) => setNewPromo((p) => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="EX2026" />
                      </div>
                      <div className="space-y-2">
                        <Label>Reduction (%)</Label>
                        <Input type="number" value={newPromo.percent} onChange={(e) => setNewPromo((p) => ({ ...p, percent: e.target.value }))} />
                      </div>
                      <Button onClick={createPromoCode} className="bg-primary hover:bg-primary-hover text-white">Creer</Button>
                      <div className="sm:col-span-3 space-y-2">
                        <Label>Note / conditions (justificatif a demander...)</Label>
                        <Input value={newPromo.note} onChange={(e) => setNewPromo((p) => ({ ...p, note: e.target.value }))} />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Reglages du site */}
              {activeTab === 'settings' && (
                <div className="space-y-6" data-testid="admin-settings">
                  <h1 className="font-heading font-bold text-2xl text-slate-900">Réglages du site</h1>
                  <p className="text-sm text-slate-500 max-w-2xl">
                    Choisis quelles rubriques apparaissent dans le menu du site public. Une rubrique
                    masquée disparaît du menu (desktop et mobile) sans supprimer son contenu.
                  </p>

                  {!siteSettings ? (
                    <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</div>
                  ) : (
                    <Card>
                      <CardHeader><CardTitle className="text-base">Rubriques visibles dans le menu</CardTitle></CardHeader>
                      <CardContent className="space-y-1">
                        {[
                          { key: 'visible_formation', label: 'Formation' },
                          { key: 'visible_pme', label: 'PME' },
                          { key: 'visible_freelance', label: 'Freelance' },
                          { key: 'visible_employes', label: 'Employés de bureau' },
                          { key: 'visible_siteweb', label: 'Site Web' },
                          { key: 'visible_testimonials', label: 'Témoignages' },
                        ].map((f) => (
                          <label key={f.key} className="flex items-center justify-between gap-2 text-sm p-3 rounded-lg hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
                            <span className="font-medium">{f.label}</span>
                            <Checkbox checked={!!siteSettings[f.key]} onCheckedChange={() => toggleSettingField(f.key)} />
                          </label>
                        ))}
                        <div className="pt-4">
                          <Button onClick={saveSettings} disabled={savingSettings} className="bg-primary hover:bg-primary-hover text-white gap-2">
                            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Enregistrer
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// WYSIWYG Editor Component
const WYSIWYGEditor = () => {
  const { t } = useLanguage();
  const editorRef = useRef(null);
  const [selectedPage, setSelectedPage] = useState('home');
  const [selectedSection, setSelectedSection] = useState('hero');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const pages = [
    { id: 'home', label: 'Accueil' },
    { id: 'formation', label: 'Formation' },
    { id: 'pme', label: 'PME' },
    { id: 'freelance', label: 'Freelance' },
    { id: 'employes', label: 'Employés' },
  ];

  const sections = [
    { id: 'hero', label: 'Hero' },
    { id: 'features', label: 'Fonctionnalités' },
    { id: 'description', label: 'Description' },
    { id: 'cta', label: 'Call to Action' },
  ];

  useEffect(() => {
    loadContent();
  }, [selectedPage, selectedSection]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${selectedPage}`);
      const sectionContent = response.data.find(c => c.section === selectedSection);
      if (sectionContent && sectionContent.content?.html) {
        setContent(sectionContent.content.html);
        if (editorRef.current) {
          editorRef.current.innerHTML = sectionContent.content.html;
        }
      } else {
        setContent('');
        if (editorRef.current) {
          editorRef.current.innerHTML = '<p>Commencez à éditer...</p>';
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/content`, {
        page: selectedPage,
        section: selectedSection,
        content: { html: editorRef.current?.innerHTML || '' }
      }, { headers: getAuthHeaders() });
      toast.success('Contenu sauvegardé');
    } catch (error) {
      toast.error(t('error'));
    }
    setSaving(false);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-slate-900">{t('manageContent')}</h1>
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary-hover text-white gap-2" data-testid="save-content-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('save')}
        </Button>
      </div>

      {/* Page & Section Selectors */}
      <div className="flex gap-4 flex-wrap">
        <div className="space-y-2">
          <label className="text-sm font-medium">Page</label>
          <div className="flex gap-2 flex-wrap">
            {pages.map((page) => (
              <Button
                key={page.id}
                variant={selectedPage === page.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPage(page.id)}
                data-testid={`page-select-${page.id}`}
              >
                {page.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Section</label>
          <div className="flex gap-2 flex-wrap">
            {sections.map((section) => (
              <Button
                key={section.id}
                variant={selectedSection === section.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedSection(section.id)}
                data-testid={`section-select-${section.id}`}
              >
                {section.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      <Card>
        <CardHeader className="border-b py-2">
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" variant="ghost" onClick={() => execCommand('bold')} title="Gras">
              <Bold className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => execCommand('italic')} title="Italique">
              <Italic className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => execCommand('formatBlock', 'h1')} title="Titre 1">
              <Heading1 className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => execCommand('formatBlock', 'h2')} title="Titre 2">
              <Heading2 className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => execCommand('insertUnorderedList')} title="Liste">
              <List className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              const url = prompt('URL du lien:');
              if (url) execCommand('createLink', url);
            }} title="Lien">
              <LinkIcon className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              const url = prompt('URL de l\'image:');
              if (url) execCommand('insertImage', url);
            }} title="Image">
              <Image className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div
            ref={editorRef}
            contentEditable
            className="wysiwyg-content min-h-[400px] p-6 focus:outline-none"
            data-testid="wysiwyg-editor"
          />
        </CardContent>
      </Card>
    </div>
  );
};
